import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  map,
  merge,
  of,
  retry,
  startWith,
  switchMap,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { Globals } from '../../globals';
import { share1UntilNoRefs } from '../../helpers';
import { AuthService } from '../../security/auth.service';
import { CamServer } from './camserver.interfaces';
import { Functionblock, FunctionblockGraph } from './functionblocks.interfaces';

@Injectable({
  providedIn: 'root',
})
export class FunctionBlocksService {
  private readonly authService = inject(AuthService);
  private readonly http = inject(HttpClient);
  private readonly globals = inject(Globals);
  private readonly reloadSmartEnergyFunctionBlocks$ = new Subject<void>();

  readonly smartEnergyFunctionBlocks$: Observable<Functionblock[] | null> =
    merge(
      this.authService.logoutEvent.pipe(map(() => true)),
      this.reloadSmartEnergyFunctionBlocks$.pipe(map(() => false)),
    ).pipe(
      startWith(false),
      switchMap((isReset) => {
        if (isReset) return of(null);
        return this.getSmartEnergyFunctionblock();
      }),
      share1UntilNoRefs,
    );

  readonly insightReportSmartEnergyFunctionBlocks$ =
    this.getSmartEnergyFunctionblocksWithDataForInsightReport();

  readonly energySmartEnergyFunctionBlocks$ =
    this.getSmartEnergyFunctionblock();

  /**
   * Get all outputs
   * @param options type: get only outputs of type
   * @returns all ouptuts of logged in user
   */
  getAll(options?: { type?: string }): Observable<Functionblock[]> {
    const { type } = options ?? {};
    const url = type
      ? `${this.globals.apiRoot}/jwtfunctionblocks?type=${type}`
      : `${this.globals.apiRoot}/jwtfunctionblocks`;
    return this.http.get<Functionblock[]>(url);
  }

  /**
   * Get functionblock by id
   * @param id Functionblock id
   */
  getFunctionblock(id: number): Observable<Functionblock> {
    const url = `${this.globals.apiRoot}/jwtfunctionblocks/${id}`;
    return this.http.get<Functionblock>(url);
  }

  getFunctionblockForSceneExtension(id: number): Observable<Functionblock> {
    const url = `${this.globals.apiRoot}/jwtfunctionblock/${id}`;
    return this.http.get<Functionblock>(url);
  }

  getFunctionblockBySerialNumber(
    serialNumber: string,
    deviceSerialNumber: string,
  ): Observable<Functionblock> {
    const url = `${this.globals.apiRoot}/jwtFunctionblocks/bySerialNumber/${serialNumber}/${deviceSerialNumber}`;
    return this.http.get<Functionblock>(url);
  }

  /**
   * Get smart energy functionblock
   */
  getSmartEnergyFunctionblock(): Observable<Functionblock[]> {
    const url = `${this.globals.apiRoot}/jwtfunctionblocks/energy`;
    return this.http.get<Functionblock[]>(url);
  }

  reloadSmartEnergyFunctionBlocks(): void {
    this.reloadSmartEnergyFunctionBlocks$.next();
  }

  /**
   * Get smart energy functionblock
   */
  getSmartEnergyFunctionblocksWithDataForInsightReport(): Observable<
    Functionblock[]
  > {
    const url = `${this.globals.apiRoot}/jwtfunctionblocks/insightReport`;
    return this.http.get<Functionblock[]>(url);
  }

  hasSmartEnergyFunctionBlock(): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtfunctionblocks/hasSmartEnergyFunctionBlock`;
    return this.http.get<boolean>(url);
  }

  /**
   * Get functionblocks by serials for smart energy dashboard
   */
  getFunctionblocksBySerials(relations: any): Observable<any> {
    const url = `${this.globals.apiRoot}/jwtfunctionblocks/serials`;
    return this.http.post<any>(url, relations);
  }

  /**
   * Updates one functionblock name, icon and readOnly status
   * @param fb the functionblock to be updated
   */
  updateFunctionblock(fb: Functionblock): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtfunctionblocks/${fb.id}`;
    return this.http.put<void>(url, fb);
  }

  bulkActionSpeech(isSpeechEnabled: boolean, ids: number[]): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtfunctionblocks/bulkActionSpeech`;
    return this.http.post<void>(url, {
      isSpeechEnabled,
      functionBlockIds: ids,
    });
  }

  bulkActionReadOnly(isReadOnly: boolean, ids: number[]): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtfunctionblocks/bulkActionReadOnly`;
    return this.http.post<void>(url, { isReadOnly, functionBlockIds: ids });
  }

  bulkActionRemoveFromGroup(groupId: number, ids: number[]): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtfunctionblocks/bulkActionRemoveFromGroup`;
    return this.http.post<void>(url, { groupId, functionBlockIds: ids });
  }

  updateFunctionblockForSceneExtention(fb: Functionblock): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtfunctionblock/${fb.id}`;
    return this.http.put<void>(url, fb);
  }

  /**
   * gets all the graphs for this functionblock
   * Only used for PWG
   * @param fbId the id of the functionblock
   */
  getFunctionblockGraph(fbId: number): Observable<FunctionblockGraph[]> {
    const url = `${this.globals.apiRoot}/jwtfunctionblocks/${fbId}/graph`;
    return this.http.get<FunctionblockGraph[]>(url);
  }

  /**
   * Init camera on the cam server
   * @param fb camera functionblock
   */
  initCamera(fb: Functionblock): Observable<CamServer> {
    const url = `${this.globals.apiRoot}/jwtfunctionblocks/${fb.id}/camera`;
    return this.http.post<CamServer>(url, fb);
  }

  /**
   * Remove camera from the cam server
   * @param fb camera functionblock
   */
  removeCamera(fb: Functionblock): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtfunctionblocks/${fb.id}/camera`;
    return this.http.delete<boolean>(url);
  }

  getSmartChargingStations(): Observable<Functionblock[]> {
    const url = `${this.globals.apiRoot}/jwtfunctionblocks/smartChargingStations`;
    return this.http.get<Functionblock[]>(url);
  }

  getSmartChargingStationsFromFirstLuqas(): Observable<Functionblock[]> {
    const url = `${this.globals.apiRoot}/jwtfunctionblocks/smartChargingStationsFromFirstLuqas`;
    return this.http.get<Functionblock[]>(url);
  }

  getSmartGridHeatPumpFromFirstLuqas(): Observable<Functionblock> {
    const url = `${this.globals.apiRoot}/jwtfunctionblocks/smartGridHeatPumpFromFirstLuqas`;
    return this.http.get<Functionblock>(url);
  }
  getSmartEnergyControllerFromFirstLuqas(): Observable<Functionblock> {
    const url = `${this.globals.apiRoot}/jwtfunctionblocks/smartEnergyControllerFromFirstLuqas`;
    return this.http.get<Functionblock>(url);
  }

  readonly isLoadingSubject = new BehaviorSubject<boolean>(false);
  isMetersAndChargingStationsLoading$: Observable<boolean> =
    this.isLoadingSubject.asObservable();

  getAllMetersAndChargingStationsFromCh$(): Observable<Functionblock[]> {
    this.isLoadingSubject.next(true);

    const url = `${this.globals.apiRoot}/jwtfunctionblocks/getFullEnergyHierarchyFromCh20`;
    const maxAttempts = 15;
    const maxRetryDelay = 60000;

    return this.http.get<any>(url).pipe(
      switchMap((data: any) => this.getAllMeters(data)),
      retry({
        count: maxAttempts,
        delay: (error, attempts) => {
          const delay = Math.min(attempts * 1000, maxRetryDelay);
          return timer(delay);
        },
      }),
      tap(() => this.isLoadingSubject.next(false)),
    );
  }

  private getAllMeters(data: any[]): Observable<Functionblock[]> {
    const meters: Functionblock[] = [];

    data.forEach((meterData) => {
      if (
        (meterData.networkDevice &&
          meterData.networkDevice.prefix === '004C') ||
        meterData.networkDevice.prefix === '004F'
      ) {
        const meter = this.extractMeters(meterData);
        meters.push(meter);
      }
      this.findChildMeters(meterData, meters);
    });

    if (!meters.some((meter) => (meter.chargingStations ?? []).length > 0)) {
      return throwError(() => new Error('No meters found'));
    }

    return of(meters);
  }

  getSmartBatteryFromFirstLuqas(): Observable<Functionblock> {
    const url = `${this.globals.apiRoot}/jwtfunctionblocks/smartBatteryFromFirstLuqas`;
    return this.http.get<Functionblock>(url);
  }

  getAllFunctionblocksFromFirstLuqas(
    types?: string[],
  ): Observable<Functionblock[]> {
    let url = `${this.globals.apiRoot}/jwtfunctionblocks/fromFirstLuqas`;

    if (types && types.length > 0) {
      const params = types
        .map((t) => `types=${encodeURIComponent(t)}`)
        .join('&');
      url += `?${params}`;
    }

    return this.http.get<Functionblock[]>(url);
  }

  private findChildMeters(meterData: any, meters: Functionblock[]): void {
    if (meterData.relations && meterData.relations.LQSCH_EnergyMeter) {
      meterData.relations.LQSCH_EnergyMeter.forEach((childMeter: any) => {
        if (
          childMeter.networkDevice &&
          childMeter.networkDevice.prefix === '004C'
        ) {
          const meter = this.extractMeters(childMeter);
          meters.push(meter);
        }
        this.findChildMeters(childMeter, meters);
      });
    }
  }

  private extractMeters(meterData: any): Functionblock {
    const meter: Functionblock = {
      id: meterData.id,
      serialNumber: meterData.serialNumber,
      name: meterData.name,
      chargingStations: [],
      type: meterData.type,
      networkDevice: {
        prefix: meterData?.networkDevice.prefix,
        serialNumber: meterData.networkDevice.serialNumber,
      },
    };

    if (
      meterData.relations &&
      meterData.relations.LQSCH_SmartChargingStations
    ) {
      meter.chargingStations =
        meterData.relations.LQSCH_SmartChargingStations.map((station: any) => ({
          id: station.id,
          serialNumber: station.serialNumber,
          name: station.name,
          type: station.type,
        }));
    }

    return meter;
  }
}
